import { z } from 'zod';
import { defineCallable } from './callable-contract';
import { InvitableOrgRoleSchema, OrgRoleSchema } from './roles';

const SuccessResultSchema = z.object({ success: z.boolean() });
const TeamRoleSchema = z.enum(['lead', 'member']);

// ---------------------------------------------------------------------------
// inviteMember
// ---------------------------------------------------------------------------

export const InviteMemberPayloadSchema = z.object({
  organizationId: z.string(),
  email: z.email().toLowerCase(),
  role: InvitableOrgRoleSchema,
});
export type InviteMemberPayload = z.infer<typeof InviteMemberPayloadSchema>;

export const InviteMemberResultSchema = z.object({
  success: z.boolean(),
  invitationId: z.string(),
});
export type InviteMemberResult = z.infer<typeof InviteMemberResultSchema>;

// ---------------------------------------------------------------------------
// updateOrganizationMemberRole
// ---------------------------------------------------------------------------

export const UpdateMemberRolePayloadSchema = z.object({
  organizationId: z.string(),
  userId: z.string(),
  role: OrgRoleSchema,
});
export type UpdateMemberRolePayload = z.infer<typeof UpdateMemberRolePayloadSchema>;

export const UpdateMemberRoleResultSchema = z.object({
  success: z.boolean(),
});
export type UpdateMemberRoleResult = z.infer<typeof UpdateMemberRoleResultSchema>;

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

export const inviteMemberContract = defineCallable('inviteMember', {
  input: InviteMemberPayloadSchema,
  output: InviteMemberResultSchema,
});

export const updateMemberRoleContract = defineCallable('updateOrganizationMemberRole', {
  input: UpdateMemberRolePayloadSchema,
  output: UpdateMemberRoleResultSchema,
});

export const DeleteOrganizationPayloadSchema = z.object({
  organizationId: z.string().min(1),
});
export type DeleteOrganizationPayload = z.infer<typeof DeleteOrganizationPayloadSchema>;

export const DeleteOrganizationResultSchema = z.object({ success: z.boolean() });
export type DeleteOrganizationResult = z.infer<typeof DeleteOrganizationResultSchema>;

export const deleteOrganizationContract = defineCallable('deleteOrganization', {
  input: DeleteOrganizationPayloadSchema,
  output: DeleteOrganizationResultSchema,
});

// ---------------------------------------------------------------------------
// addTeamMember
// ---------------------------------------------------------------------------

export const AddTeamMemberPayloadSchema = z.object({
  organizationId: z.string().min(1),
  teamId: z.string().min(1),
  userId: z.string().min(1),
  role: TeamRoleSchema.default('member'),
});
export type AddTeamMemberPayload = z.infer<typeof AddTeamMemberPayloadSchema>;

export const addTeamMemberContract = defineCallable('addTeamMember', {
  input: AddTeamMemberPayloadSchema,
  output: SuccessResultSchema,
});

// ---------------------------------------------------------------------------
// createTeam
// ---------------------------------------------------------------------------

export const CreateTeamPayloadSchema = z.object({
  organizationId: z.string().min(1),
  name: z.string().min(3).max(100),
  description: z.string().max(500).optional(),
});
export type CreateTeamPayload = z.infer<typeof CreateTeamPayloadSchema>;

export const CreateTeamResultSchema = z.object({
  success: z.boolean(),
  teamId: z.string(),
});
export type CreateTeamResult = z.infer<typeof CreateTeamResultSchema>;

export const createTeamContract = defineCallable('createTeam', {
  input: CreateTeamPayloadSchema,
  output: CreateTeamResultSchema,
});

// ---------------------------------------------------------------------------
// deleteTeam
// ---------------------------------------------------------------------------

export const DeleteTeamPayloadSchema = z.object({
  organizationId: z.string().min(1),
  teamId: z.string().min(1),
});
export type DeleteTeamPayload = z.infer<typeof DeleteTeamPayloadSchema>;

export const deleteTeamContract = defineCallable('deleteTeam', {
  input: DeleteTeamPayloadSchema,
  output: SuccessResultSchema,
});

// ---------------------------------------------------------------------------
// updateTeam
// ---------------------------------------------------------------------------

export const UpdateTeamPayloadSchema = z.object({
  organizationId: z.string().min(1),
  teamId: z.string().min(1),
  name: z.string().min(3).max(100),
  description: z.string().max(500).optional(),
});
export type UpdateTeamPayload = z.infer<typeof UpdateTeamPayloadSchema>;

export const updateTeamContract = defineCallable('updateTeam', {
  input: UpdateTeamPayloadSchema,
  output: SuccessResultSchema,
});

// ---------------------------------------------------------------------------
// updateTeamMemberRole
// ---------------------------------------------------------------------------

export const UpdateTeamMemberRolePayloadSchema = z.object({
  organizationId: z.string().min(1),
  teamId: z.string().min(1),
  userId: z.string().min(1),
  role: TeamRoleSchema,
});
export type UpdateTeamMemberRolePayload = z.infer<typeof UpdateTeamMemberRolePayloadSchema>;

export const updateTeamMemberRoleContract = defineCallable('updateTeamMemberRole', {
  input: UpdateTeamMemberRolePayloadSchema,
  output: SuccessResultSchema,
});

// ---------------------------------------------------------------------------
// removeTeamMember
// ---------------------------------------------------------------------------

export const RemoveTeamMemberPayloadSchema = z.object({
  organizationId: z.string().min(1),
  teamId: z.string().min(1),
  userId: z.string().min(1),
});
export type RemoveTeamMemberPayload = z.infer<typeof RemoveTeamMemberPayloadSchema>;

export const removeTeamMemberContract = defineCallable('removeTeamMember', {
  input: RemoveTeamMemberPayloadSchema,
  output: SuccessResultSchema,
});

// ---------------------------------------------------------------------------
// removeOrganizationMember
// ---------------------------------------------------------------------------

export const RemoveOrganizationMemberPayloadSchema = z.object({
  organizationId: z.string().min(1),
  userId: z.string().min(1),
});
export type RemoveOrganizationMemberPayload = z.infer<typeof RemoveOrganizationMemberPayloadSchema>;

export const removeOrganizationMemberContract = defineCallable('removeOrganizationMember', {
  input: RemoveOrganizationMemberPayloadSchema,
  output: SuccessResultSchema,
});

// ---------------------------------------------------------------------------
// resendInvitation
// ---------------------------------------------------------------------------

export const ResendInvitationPayloadSchema = z.object({
  organizationId: z.string().min(1),
  invitationId: z.string().min(1),
});
export type ResendInvitationPayload = z.infer<typeof ResendInvitationPayloadSchema>;

export const resendInvitationContract = defineCallable('resendInvitation', {
  input: ResendInvitationPayloadSchema,
  output: SuccessResultSchema,
});

// ---------------------------------------------------------------------------
// revokeInvitation
// ---------------------------------------------------------------------------

export const RevokeInvitationPayloadSchema = z.object({
  organizationId: z.string().min(1),
  invitationId: z.string().min(1),
});
export type RevokeInvitationPayload = z.infer<typeof RevokeInvitationPayloadSchema>;

export const revokeInvitationContract = defineCallable('revokeInvitation', {
  input: RevokeInvitationPayloadSchema,
  output: SuccessResultSchema,
});

// ---------------------------------------------------------------------------
// provisionIntakeEmail
// ---------------------------------------------------------------------------

export const ProvisionIntakeEmailPayloadSchema = z.object({
  organizationId: z.string().min(1),
});
export type ProvisionIntakeEmailPayload = z.infer<typeof ProvisionIntakeEmailPayloadSchema>;

export const ProvisionIntakeEmailResultSchema = z.object({
  intakeEmail: z.string(),
});
export type ProvisionIntakeEmailResult = z.infer<typeof ProvisionIntakeEmailResultSchema>;

export const provisionIntakeEmailContract = defineCallable('provisionIntakeEmail', {
  input: ProvisionIntakeEmailPayloadSchema,
  output: ProvisionIntakeEmailResultSchema,
});

// ---------------------------------------------------------------------------
// updateOrganization
// ---------------------------------------------------------------------------

export const UpdateOrganizationPayloadSchema = z.object({
  organizationId: z.string().min(1),
  name: z.string().trim().min(3).max(100).optional(),
  description: z.string().max(500).optional(),
  settings: z
    .object({
      allowMemberInvites: z.boolean().optional(),
      defaultMemberRole: InvitableOrgRoleSchema.optional(),
      verificationsVisibleToMembers: z.boolean().optional(),
      intake: z
        .object({
          includeSituations: z.boolean(),
          includeMatters: z.boolean(),
          includeQualifiers: z.boolean(),
          includeUrgency: z.boolean(),
          emailIntakeEnabled: z.boolean().optional(),
        })
        .optional(),
    })
    .optional(),
});
export type UpdateOrganizationPayload = z.infer<typeof UpdateOrganizationPayloadSchema>;

export const updateOrganizationContract = defineCallable('updateOrganization', {
  input: UpdateOrganizationPayloadSchema,
  output: SuccessResultSchema,
});

// ---------------------------------------------------------------------------
// visitorHistory
// ---------------------------------------------------------------------------
//
// User-scoped (no orgId). Wraps FingerprintJS Pro Server API's `getVisits`.
// The output schema mirrors the FingerprintJS `VisitorsResponse` shape so
// frontend and backend share one source of truth.

export const VisitorHistoryPayloadSchema = z.object({
  visitorId: z.string().min(1),
});
export type VisitorHistoryPayload = z.infer<typeof VisitorHistoryPayloadSchema>;

// Mirrors the FingerprintJS Pro Server API `VisitorsResponse` shape. Optionality
// follows the SDK's TypeScript types — `confidence` itself is optional, with
// optional `revision` and `comment` inside; `components`, `sdk`, and
// `environmentId` are optional. `.loose()` on the visit object lets future SDK
// additions through without breaking parsing (the SDK's own changelog warns
// that components and adjacent fields can shift). Frontend can tighten via
// its own parse if it wants stricter guarantees on a specific field.
export const VisitorHistoryResultSchema = z.object({
  visitorId: z.string(),
  visits: z.array(
    z
      .object({
        requestId: z.string(),
        browserDetails: z.object({
          browserName: z.string(),
          browserMajorVersion: z.string(),
          browserFullVersion: z.string(),
          os: z.string(),
          osVersion: z.string(),
          device: z.string(),
          userAgent: z.string(),
        }),
        incognito: z.boolean(),
        ip: z.string(),
        timestamp: z.number(),
        time: z.string(),
        url: z.string(),
        tag: z.object({}).loose(),
        confidence: z
          .object({
            score: z.number(),
            revision: z.string().optional(),
            comment: z.string().optional(),
          })
          .optional(),
        visitorFound: z.boolean(),
        firstSeenAt: z.object({
          global: z.string().nullable(),
          subscription: z.string().nullable(),
        }),
        lastSeenAt: z.object({
          global: z.string().nullable(),
          subscription: z.string().nullable(),
        }),
        replayed: z.boolean().optional(),
      })
      .loose(),
  ),
  lastTimestamp: z.number().optional(),
  paginationKey: z.string().optional(),
});
export type VisitorHistoryResult = z.infer<typeof VisitorHistoryResultSchema>;

export const visitorHistoryContract = defineCallable('visitorHistory', {
  input: VisitorHistoryPayloadSchema,
  output: VisitorHistoryResultSchema,
});

export const acceptInvitationContract = defineCallable('acceptInvitation', {
  // Mirrors backend enforcement in functions/src/organizations/accept-invitation.ts.
  // Token is a 32-byte hex string (64 chars) generated in email-service.ts.
  input: z.object({ token: z.string().length(64) }),
  output: z.object({ success: z.boolean(), organizationId: z.string(), slug: z.string() }),
});

export const checkWhitelistStatusContract = defineCallable('checkWhitelistStatus', {
  input: z.object({ email: z.email().toLowerCase() }),
  output: z.object({ whitelisted: z.boolean() }),
});
